import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@agissc/ktag-map';
import { useTracking } from '../../utils/tracking/TrackingContext';
import useAppState from '../../AppStore';
import ToolDialogBackContext from './ToolDialogBackContext';

interface ToolDialogViewProps {
  isMainView: boolean;
  label: string;
  viewName: string;
  viewComponent?: React.ReactNode;
  toolName: string;
  goToMainView: () => void;
  onClose: () => void;
}

const ToolDialogView = ({
  isMainView,
  label,
  viewComponent,
  viewName: key,
  toolName,
  onClose,
  goToMainView,
}: ToolDialogViewProps) => {
  const { t } = useTranslation('agisviewer');
  const activeFlyin = useAppState((state) => state.activeFlyin);
  const { Tracking } = useTracking();

  const [backFn, setBackFn] = useState<(() => void) | null>(null);
  const contextValue = useMemo(() => ({ backFn, setBackFn }), [backFn, setBackFn]);

  const handleClose = useCallback(() => {
    Tracking.Tool.closeTool(toolName, 'ToolDialog');
    onClose();
  }, [Tracking, onClose, toolName]);

  const handleBackOnClick = useCallback(() => {
    if (backFn) {
      backFn();
      return;
    }

    if (key === 'flyin') {
      Tracking.UI.returnToMainView(activeFlyin ?? 'unknownFlyin', toolName);
    } else {
      Tracking.UI.returnToMainView(toolName, key);
    }
    goToMainView();
  }, [Tracking, goToMainView, toolName, backFn]);

  return (
    <ToolDialogBackContext.Provider value={contextValue}>
      <div className="tooldialog">
        <div className="tooldialog__view">
          <div className="tooldialog__header">
            {!isMainView && (
              <IconButton
                onClick={handleBackOnClick}
                title={t('tool_back')}
                icon="arrow-left"
                additionalClassNames="tooldialog__header-button"
              />
            )}
            <h1>{t(label)}</h1>
            <IconButton
              onClick={handleClose}
              title={t('tool_close')}
              icon="cross"
              additionalClassNames="tooldialog__header-button"
            />
          </div>
          <div className="tooldialog__content">{viewComponent}</div>
        </div>
      </div>
    </ToolDialogBackContext.Provider>
  );
};

export default ToolDialogView;
